import { useMemo } from "react"
import { useAuth } from "@/contexts/auth-context"
import { env } from "@/lib/env"
import { getBasePathUrl } from "@/lib/base-path"
import { requestTokenDueToAuthFailure } from "@/lib/app-messaging"
import { tokenManager } from "@/lib/token-manager"

// API endpoints
export const API_ENDPOINTS = {
  // Main API
  MAIN: {
    BASE_URL: env.apiUrl,
    // Add specific endpoints here
    USERS: "/users",
    // ...
  },
  // Admin Service API
  ADMIN: {
    BASE_URL: env.adminServiceApiEndpoint,
    // Add specific endpoints here
    SETTINGS: "/settings",
    // ...
  },
  // Engagement API
  ENGAGEMENT: {
    BASE_URL: env.engagementApiUrl,
    // Add specific endpoints here
    // ...
  },
  // Reports API
  REPORTS: {
    BASE_URL: env.reportsApiUrl,
    // Add specific endpoints here
    // ...
  },
  // People API
  PEOPLE: {
    BASE_URL: env.peopleApiUrl,
    // Add specific endpoints here
    // ...
  },
  // Organization API
  ORGANIZATION: {
    BASE_URL: env.organizationApiUrl,
    // Add specific endpoints here
    // ...
  },
  // Audit API
  AUDIT: {
    BASE_URL: env.auditApiUrl,
    // Add specific endpoints here
    // ...
  },
}

// Log API endpoints for debugging
if (typeof window !== "undefined") {
  console.log("API_ENDPOINTS:", API_ENDPOINTS)
}

/**
 * A request the server refused, carrying the status alongside the message so a caller can tell a
 * rejected save from a service that fell over and word it accordingly.
 */
export class ApiError extends Error {
  constructor(message: string, public readonly status: number) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * The status an error carries, or 500 for one that carries none. A caller wrapping an error in its own
 * type uses this to keep the status rather than inventing one: a refused save and a service that fell
 * over are different things to say to a user.
 */
export const statusOf = (error: unknown): number =>
  typeof error === 'object' && error !== null && typeof (error as { status?: unknown }).status === 'number'
    ? (error as { status: number }).status
    : 500

/**
 * What an error response is trying to say, read off the body as text first.
 *
 * Parsing it as JSON is what hid these messages: a rejected save answers `BadRequest(ex.Message)`, and
 * ASP.NET serves a bare string as text/plain, so `response.json()` throws and the reason is swallowed
 * along with it. Text parses either way.
 */
export async function messageFromResponse(response: Response): Promise<string | null> {
  const body = await response.text().catch(() => '')

  if (!body.trim()) {
    return null
  }

  try {
    return messageFromErrorBody(JSON.parse(body))
  } catch {
    // Not JSON at all, which is what text/plain gives us.
    return body.trim()
  }
}

/**
 * What an error response is trying to say, whichever shape it arrived in.
 *
 * The services do not agree on one. A rejected save answers `BadRequest(ex.Message)`, which serializes
 * as a bare JSON string rather than an object, so reading `.message` off it finds nothing and the user
 * is told only that a request failed with a status code, while the reason sits unread in the response.
 * ProblemDetails and a plain `{ message }` both turn up too.
 */
export function messageFromErrorBody(body: unknown): string | null {
  if (typeof body === 'string') {
    return body.trim() || null
  }

  if (!body || typeof body !== 'object') {
    return null
  }

  const shape = body as { message?: unknown; detail?: unknown; title?: unknown; errors?: unknown }

  for (const candidate of [shape.message, shape.detail, shape.title]) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim()
    }
  }

  // ModelState-style: { errors: { field: ["first", "second"] } }
  if (shape.errors && typeof shape.errors === 'object') {
    const messages = Object.values(shape.errors as Record<string, unknown>)
      .flatMap(value => (Array.isArray(value) ? value : [value]))
      .filter((value): value is string => typeof value === 'string' && !!value.trim())

    if (messages.length > 0) {
      return messages.join(' ')
    }
  }

  return null
}

// Custom hook for making authenticated API requests
export function useApi() {
  const { token } = useAuth()

  // Generic fetch function with authentication and token retry logic
  const fetchWithAuth = async <T,>(url: string, options: RequestInit = {}): Promise<T> => {
    const headers = new Headers(options.headers)

    // Add authorization header if token exists
    if (token) {
      headers.set("Authorization", `Bearer ${token}`)
    }

    // Apply base path if the URL is relative and doesn't start with http or //
    let finalUrl = url
    if (!url.startsWith("http") && !url.startsWith("//")) {
      finalUrl = getBasePathUrl(url)
    }

    try {
      const response = await fetch(finalUrl, {
        ...options,
        headers,
      })

      if (!response.ok) {
        // Handle 401 Unauthorized errors
        if (response.status === 401) {
          console.warn("Received 401 Unauthorized from", finalUrl)

          // Only request a new token if we haven't already requested one
          if (tokenManager.shouldRequestToken()) {
            console.log("Requesting new token from parent")
            requestTokenDueToAuthFailure(`401 Unauthorized from ${finalUrl}`)
          }

          // Queue this request to be retried when we get a new token
          console.log("Queueing request for retry when new token is received")
          const retriedResponse = await tokenManager.queueRequest(finalUrl, {
            ...options,
            headers,
          })

          // Process the retried response
          if (!retriedResponse.ok) {
            throw new ApiError(
              (await messageFromResponse(retriedResponse)) || `API request failed with status ${retriedResponse.status}`,
              retriedResponse.status)
          }

          // For non-JSON responses, return the response object
          const contentType = retriedResponse.headers.get("content-type")
          if (contentType && contentType.includes("application/json")) {
            return retriedResponse.json()
          }

          return retriedResponse as unknown as T
        }

        // Handle other error responses
        throw new ApiError(
          (await messageFromResponse(response)) || `API request failed with status ${response.status}`,
          response.status)
      }

      // For non-JSON responses, return the response object
      const contentType = response.headers.get("content-type")
      if (contentType && contentType.includes("application/json")) {
        return response.json()
      }

      return response as unknown as T
    } catch (error) {
      // Rethrow the error
      throw error
    }
  }

  // Helper methods for common HTTP methods
  const get = <T,>(endpoint: string, options: RequestInit = {}) =>
    fetchWithAuth<T>(endpoint, { ...options, method: "GET" })

  const post = <T,>(endpoint: string, data: any, options: RequestInit = {}) =>
    fetchWithAuth<T>(endpoint, {
      ...options,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
      body: JSON.stringify(data),
    })

  const put = <T,>(endpoint: string, data: any, options: RequestInit = {}) =>
    fetchWithAuth<T>(endpoint, {
      ...options,
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
      body: JSON.stringify(data),
    })

  const patch = <T,>(endpoint: string, data: any, options: RequestInit = {}) =>
    fetchWithAuth<T>(endpoint, {
      ...options,
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
      body: JSON.stringify(data),
    })

  const del = <T,>(endpoint: string, options: RequestInit = {}) =>
    fetchWithAuth<T>(endpoint, { ...options, method: "DELETE" })

  // Create full URLs for each API
  const createUrl = (baseUrl: string, path: string) => {
    // Validate baseUrl is a real URL
    if (!baseUrl.startsWith("http")) {
      throw new Error(`Invalid baseUrl: ${baseUrl}`)
    }

    // Remove trailing slash from base URL if present
    const base = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl
    // Remove leading slash from path if present
    const cleanPath = path.startsWith("/") ? path.slice(1) : path
    return `${base}/${cleanPath}`
  }

  // API-specific methods (memoized for stable references)
  const mainApi = useMemo(
    () => ({
      get: <T,>(path: string, options?: RequestInit) => get<T>(createUrl(API_ENDPOINTS.MAIN.BASE_URL, path), options),
      post: <T,>(path: string, data: any, options?: RequestInit) =>
        post<T>(createUrl(API_ENDPOINTS.MAIN.BASE_URL, path), data, options),
      put: <T,>(path: string, data: any, options?: RequestInit) =>
        put<T>(createUrl(API_ENDPOINTS.MAIN.BASE_URL, path), data, options),
      patch: <T,>(path: string, data: any, options?: RequestInit) =>
        patch<T>(createUrl(API_ENDPOINTS.MAIN.BASE_URL, path), data, options),
      delete: <T,>(path: string, options?: RequestInit) => del<T>(createUrl(API_ENDPOINTS.MAIN.BASE_URL, path), options),
    }),
    []
  )

  const adminApi = useMemo(
    () => ({
      get: <T,>(path: string, options?: RequestInit) => get<T>(createUrl(API_ENDPOINTS.ADMIN.BASE_URL, path), options),
      post: <T,>(path: string, data: any, options?: RequestInit) =>
        post<T>(createUrl(API_ENDPOINTS.ADMIN.BASE_URL, path), data, options),
      put: <T,>(path: string, data: any, options?: RequestInit) =>
        put<T>(createUrl(API_ENDPOINTS.ADMIN.BASE_URL, path), data, options),
      patch: <T,>(path: string, data: any, options?: RequestInit) =>
        patch<T>(createUrl(API_ENDPOINTS.ADMIN.BASE_URL, path), data, options),
      delete: <T,>(path: string, options?: RequestInit) => del<T>(createUrl(API_ENDPOINTS.ADMIN.BASE_URL, path), options),
    }),
    []
  )

  const engagementApi = useMemo(
    () => ({
      get: <T,>(path: string, options?: RequestInit) => get<T>(createUrl(API_ENDPOINTS.ENGAGEMENT.BASE_URL, path), options),
      post: <T,>(path: string, data: any, options?: RequestInit) =>
        post<T>(createUrl(API_ENDPOINTS.ENGAGEMENT.BASE_URL, path), data, options),
      put: <T,>(path: string, data: any, options?: RequestInit) =>
        put<T>(createUrl(API_ENDPOINTS.ENGAGEMENT.BASE_URL, path), data, options),
      patch: <T,>(path: string, data: any, options?: RequestInit) =>
        patch<T>(createUrl(API_ENDPOINTS.ENGAGEMENT.BASE_URL, path), data, options),
      delete: <T,>(path: string, options?: RequestInit) => del<T>(createUrl(API_ENDPOINTS.ENGAGEMENT.BASE_URL, path), options),
    }),
    []
  )

  const reportsApi = useMemo(
    () => ({
      get: <T,>(path: string, options?: RequestInit) => get<T>(createUrl(API_ENDPOINTS.REPORTS.BASE_URL, path), options),
      post: <T,>(path: string, data: any, options?: RequestInit) =>
        post<T>(createUrl(API_ENDPOINTS.REPORTS.BASE_URL, path), data, options),
      put: <T,>(path: string, data: any, options?: RequestInit) =>
        put<T>(createUrl(API_ENDPOINTS.REPORTS.BASE_URL, path), data, options),
      patch: <T,>(path: string, data: any, options?: RequestInit) =>
        patch<T>(createUrl(API_ENDPOINTS.REPORTS.BASE_URL, path), data, options),
      delete: <T,>(path: string, options?: RequestInit) => del<T>(createUrl(API_ENDPOINTS.REPORTS.BASE_URL, path), options),
    }),
    []
  )

  const peopleApi = useMemo(
    () => ({
      get: <T,>(path: string, options?: RequestInit) => get<T>(createUrl(API_ENDPOINTS.PEOPLE.BASE_URL, path), options),
      post: <T,>(path: string, data: any, options?: RequestInit) =>
        post<T>(createUrl(API_ENDPOINTS.PEOPLE.BASE_URL, path), data, options),
      put: <T,>(path: string, data: any, options?: RequestInit) =>
        put<T>(createUrl(API_ENDPOINTS.PEOPLE.BASE_URL, path), data, options),
      patch: <T,>(path: string, data: any, options?: RequestInit) =>
        patch<T>(createUrl(API_ENDPOINTS.PEOPLE.BASE_URL, path), data, options),
      delete: <T,>(path: string, options?: RequestInit) => del<T>(createUrl(API_ENDPOINTS.PEOPLE.BASE_URL, path), options),
    }),
    []
  )

  const organizationApi = useMemo(
    () => ({
      get: <T,>(path: string, options?: RequestInit) => get<T>(createUrl(API_ENDPOINTS.ORGANIZATION.BASE_URL, path), options),
      post: <T,>(path: string, data: any, options?: RequestInit) =>
        post<T>(createUrl(API_ENDPOINTS.ORGANIZATION.BASE_URL, path), data, options),
      put: <T,>(path: string, data: any, options?: RequestInit) =>
        put<T>(createUrl(API_ENDPOINTS.ORGANIZATION.BASE_URL, path), data, options),
      patch: <T,>(path: string, data: any, options?: RequestInit) =>
        patch<T>(createUrl(API_ENDPOINTS.ORGANIZATION.BASE_URL, path), data, options),
      delete: <T,>(path: string, options?: RequestInit) => del<T>(createUrl(API_ENDPOINTS.ORGANIZATION.BASE_URL, path), options),
    }),
    []
  )

  const auditApi = useMemo(
    () => ({
      get: <T,>(path: string, options?: RequestInit) => get<T>(createUrl(API_ENDPOINTS.AUDIT.BASE_URL, path), options),
      post: <T,>(path: string, data: any, options?: RequestInit) =>
        post<T>(createUrl(API_ENDPOINTS.AUDIT.BASE_URL, path), data, options),
      put: <T,>(path: string, data: any, options?: RequestInit) =>
        put<T>(createUrl(API_ENDPOINTS.AUDIT.BASE_URL, path), data, options),
      patch: <T,>(path: string, data: any, options?: RequestInit) =>
        patch<T>(createUrl(API_ENDPOINTS.AUDIT.BASE_URL, path), data, options),
      delete: <T,>(path: string, options?: RequestInit) => del<T>(createUrl(API_ENDPOINTS.AUDIT.BASE_URL, path), options),
    }),
    []
  )

  return {
    mainApi,
    adminApi,
    engagementApi,
    reportsApi,
    peopleApi,
    organizationApi,
    auditApi,
    fetchWithAuth,
  }
}
